import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationDeletePlayerTag from '../../../../api/hooks/useMutationDeletePlayerTag'
import { TagsQueryKeys } from '../../../../constants'
import type { PlayerTagResponse } from '../../../../types'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'

interface DeletePlayerTagModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	tag: PlayerTagResponse
}

export default function DeletePlayerTagModal({
	open,
	onOpenChange,
	tag,
}: DeletePlayerTagModalProps) {
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)

	const { mutateAsync: deleteTag } = useMutationDeletePlayerTag()

	const handleDelete = async () => {
		try {
			setIsLoading(true)

			await deleteTag({ id: tag.id })

			toast.success('ลบแท็กสำเร็จ')

			// Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTags],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTagStatistics],
			})
			queryClient.invalidateQueries({
				queryKey: [PlayerQueryKeys.GetPlayerList],
			})

			onOpenChange(false)
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>ยืนยันการลบแท็ก</DialogTitle>
					<DialogDescription>
						คุณแน่ใจหรือไม่ที่จะลบแท็ก &quot;{tag.name}&quot;?
					</DialogDescription>
				</DialogHeader>

				<div className="py-4">
					<p className="text-sm text-muted-foreground">
						แท็กนี้มีผู้เล่น {tag.playerCount} คน การลบแท็กจะไม่ลบข้อมูลผู้เล่น
						แต่จะลบแท็กออกจากผู้เล่นทั้งหมด
					</p>
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isLoading}
					>
						ยกเลิก
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={isLoading}
					>
						{isLoading ? 'กำลังลบ...' : 'ลบแท็ก'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
